// src/pages/SupplyChainManage.jsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Search as SearchIcon,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  X,
  Network,
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import { useSidebarCollapsed } from "../utils/useSidebarCollapsed";
import { redirectToLogin } from "../utils/auth";
import { useSupplierSecurityAccess } from "../utils/brandProtection";
import {
  FLAG,
  COUNTRY_OPTIONS,
  SIZE_OPTIONS,
  INDUSTRY_OPTIONS,
  loadCompanies,
  saveCompanies,
} from "../data/supplyChainCompanies";

// Backend track-supplier endpoint. POST {domain, display_name?, size?, country?,
// industry?, category?, notes?, supplier_tier?} → 200 with usage counters, or
// 4xx with {error, …}. 403 with `limit` set means the org's supplier quota is
// exhausted; we surface used/limit in the inline error so the analyst knows
// what to do (archive one to free a slot).
const ADD_TRACKED_SUPPLIER_URL =
  "https://whiteintel.io/api/regular/app/v2/supply_chain/add_tracked_supplier.php";

// Backend remove-supplier endpoint. POST {id?, domain?, hard?} → 200 with
// usage counters, or 4xx with {error}. Defaults to soft delete (archive),
// which is reversible by re-adding the supplier. We use id when the row
// has one (faster + survives domain renormalization) and fall back to
// domain otherwise.
const DELETE_TRACKED_SUPPLIER_URL =
  "https://whiteintel.io/api/regular/app/v2/supply_chain/delete_tracked_supplier.php";

function getJwtToken() {
  try {
    return (
      localStorage.getItem("jwtToken") ||
      localStorage.getItem("token") ||
      sessionStorage.getItem("jwtToken") ||
      window.WHITEINTEL_TOKEN ||
      ""
    );
  } catch {
    return "";
  }
}

const panel = "rounded-md border border-sky-900/30 bg-[#0a1224]/70";

const primaryBtn =
  "inline-flex items-center gap-1.5 rounded-md bg-sky-600 hover:bg-sky-500 px-3.5 py-1.5 text-[13px] font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors";

const ghostBtn =
  "inline-flex items-center gap-1.5 rounded-md border border-sky-900/30 hover:bg-sky-900/30 px-2.5 py-1.5 text-[12px] text-neutral-300 hover:text-sky-300 transition-colors";

const inputClass =
  "w-full rounded-md border border-sky-900/30 bg-[#0b1426] px-3 py-2 text-[13px] text-white outline-none focus:border-sky-500/50 transition-colors disabled:opacity-50";

const labelClass =
  "block mb-1 text-[11px] uppercase tracking-wider text-neutral-500 font-medium";

function todayStamp() {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${mm}/${dd}/${d.getFullYear()}`;
}

function normalizeDomain(input) {
  return input
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .replace(/\/.*$/, "");
}

function inferName(domain) {
  const root = domain.split(".")[0] || "";
  return root
    .split(/[-_]/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function SupplierUpgradeRequiredCta() {
  const navigate = useNavigate();
  return (
    <div className="max-w-2xl mx-auto mt-12 px-4">
      <div className="rounded-md border border-sky-900/30 bg-[#0a1224]/70 backdrop-blur-md shadow-[0_0_10px_rgba(56,189,248,0.05)] p-8 text-center">
        <div className="mx-auto inline-flex rounded-full border border-sky-500/30 bg-sky-500/10 p-3 mb-4">
          <Network className="h-6 w-6 text-sky-300" />
        </div>
        <h2 className="text-xl font-semibold text-white">
          Supplier Security is a premium module
        </h2>
        <p className="mt-2 text-sm text-neutral-400 leading-relaxed">
          Continuous credential-exposure scoring for your vendors and partners.
          Available on{" "}
          <span className="text-sky-200 font-medium">Enterprise</span>{" "}
          (64 suppliers),{" "}
          <span className="text-sky-200 font-medium">Threat Intelligence</span>{" "}
          (128), and{" "}
          <span className="text-sky-200 font-medium">Extended Threat Intelligence</span>{" "}
          (256).
        </p>
        <div className="mt-6 flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => navigate("/upgrades")}
            className="inline-flex items-center gap-1.5 rounded-lg border border-sky-600/40 bg-sky-500/15 px-4 py-2 text-sm font-semibold text-sky-100 hover:bg-sky-500/25 transition-colors"
          >
            View plans
          </button>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="rounded-lg border border-sky-900/40 px-4 py-2 text-sm text-neutral-200 hover:text-white hover:bg-white/[0.04] transition-colors"
          >
            Back to dashboard
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SupplyChainManage() {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useSidebarCollapsed();
  const [mobileOpen, setMobileOpen] = useState(false);

  const supplierAccess = useSupplierSecurityAccess();
  // Server-side org gate. Either supplier endpoint can return
  // "You must belong to an organization …" — flipping this on swaps the
  // page body to the same upgrade CTA the entitlement gate uses.
  const [orgGate, setOrgGate] = useState(false);
  const gateActive =
    (!supplierAccess.loading && supplierAccess.available === false) || orgGate;

  const [rows, setRows] = useState(() => loadCompanies());
  const [query, setQuery] = useState("");

  // Add form
  const [domain, setDomain] = useState("");
  const [name, setName] = useState("");
  const [size, setSize] = useState("201 – 500");
  const [country, setCountry] = useState("US");
  const [industry, setIndustry] = useState("Software & SaaS");

  // Feedback
  const [notice, setNotice] = useState(null); // { kind: 'success' | 'error', msg }
  // Pending POST flag — keeps the button busy while the API call is in flight.
  const [submitting, setSubmitting] = useState(false);
  // Confirm-delete state — domain currently awaiting confirmation
  const [confirmRemove, setConfirmRemove] = useState(null);
  // Domain currently being removed (API in flight) — disables both confirm
  // buttons in the row so the user can't double-click.
  const [removingDomain, setRemovingDomain] = useState(null);

  useEffect(() => {
    saveCompanies(rows);
  }, [rows]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        r.domain.toLowerCase().includes(q) ||
        r.country.toLowerCase().includes(q)
    );
  }, [rows, query]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const clean = normalizeDomain(domain);
    if (!clean) {
      setNotice({ kind: "error", msg: "Domain is required." });
      return;
    }
    if (!/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(clean)) {
      setNotice({ kind: "error", msg: "Enter a valid domain (e.g. acme.com)." });
      return;
    }

    const trimmedName = name.trim();
    setSubmitting(true);
    setNotice(null);
    try {
      const res = await fetch(ADD_TRACKED_SUPPLIER_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getJwtToken()}`,
        },
        body: JSON.stringify({
          domain: clean,
          display_name: trimmedName || null,
          size,
          country,
          industry,
        }),
      });
      if (res.status === 401) {
        redirectToLogin();
        return;
      }

      const text = await res.text();
      let payload = null;
      try {
        payload = text ? JSON.parse(text) : null;
      } catch {
        payload = null;
      }

      if (!res.ok) {
        // 403 with limit data → surface the quota so the analyst knows what to do.
        let msg = payload?.error || `Request failed (HTTP ${res.status}).`;
        if (/must belong to an organization/i.test(msg)) {
          setOrgGate(true);
          return;
        }
        if (
          res.status === 403 &&
          payload &&
          payload.limit !== undefined &&
          payload.used !== undefined
        ) {
          msg = `${payload.error} Using ${payload.used}/${payload.limit} supplier slots — archive one to free a slot.`;
        }
        setNotice({ kind: "error", msg });
        return;
      }

      // The backend may have normalized the domain (e.g. www. stripping,
      // public-suffix resolution). Trust its `domain` field over the input.
      const finalDomain = payload?.domain || clean;
      const finalName = trimmedName || inferName(finalDomain);

      const newRow = {
        domain: finalDomain,
        name: finalName,
        sighting: "—",
        identity: 0,
        malware: { v: 0, d: todayStamp() },
        breach: { v: 0, d: todayStamp() },
        phish: { v: 0, d: todayStamp() },
        combo: { v: 0, d: todayStamp() },
        size,
        country,
        industry,
        trend: null,
        added: todayStamp(),
        supplier_id: payload?.supplier_id,
      };

      // If the domain already exists in the local cache (e.g. reactivation),
      // replace that row in place; otherwise prepend the new one.
      setRows((prev) => {
        const without = prev.filter((r) => r.domain !== finalDomain);
        return [newRow, ...without];
      });
      setDomain("");
      setName("");

      const verb = payload?.action === "reactivated" ? "reactivated" : "added";
      const slots =
        payload?.used !== undefined && payload?.limit !== undefined
          ? ` Using ${payload.used}/${payload.limit} supplier slots.`
          : "";
      setNotice({
        kind: "success",
        msg: `${finalName} ${verb} to monitoring.${slots}`,
      });
    } catch (err) {
      setNotice({
        kind: "error",
        msg: err?.message || "Network error — could not add supplier.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (target) => {
    if (removingDomain) return;
    // Look up the row so we can send the supplier_id (set after add()) when
    // we have it; the backend prefers id over domain. We always hard-delete
    // — soft archive has no companion UI yet, so it would be a dead end.
    const row = rows.find((r) => r.domain === target);
    setRemovingDomain(target);
    setNotice(null);
    try {
      const body = row?.supplier_id
        ? { id: row.supplier_id, hard: true }
        : { domain: target, hard: true };
      const res = await fetch(DELETE_TRACKED_SUPPLIER_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getJwtToken()}`,
        },
        body: JSON.stringify(body),
      });
      if (res.status === 401) {
        redirectToLogin();
        return;
      }

      const text = await res.text();
      let payload = null;
      try {
        payload = text ? JSON.parse(text) : null;
      } catch {
        payload = null;
      }

      if (!res.ok) {
        const msg = payload?.error || `Could not remove ${target} (HTTP ${res.status}).`;
        if (/must belong to an organization/i.test(msg)) {
          setOrgGate(true);
          return;
        }
        setNotice({ kind: "error", msg });
        return;
      }

      setRows((prev) => prev.filter((r) => r.domain !== target));
      setConfirmRemove(null);

      const slots =
        payload?.used !== undefined && payload?.limit !== undefined
          ? ` Using ${payload.used}/${payload.limit} supplier slots.`
          : "";
      setNotice({
        kind: "success",
        msg: `${target} removed from monitoring.${slots}`,
      });
    } catch (err) {
      setNotice({
        kind: "error",
        msg: err?.message || "Network error — could not remove supplier.",
      });
    } finally {
      setRemovingDomain(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#000414] text-white">
      <header
        className="bg-[#0a1224]/85 border-b border-sky-800/40 backdrop-blur sticky top-0 z-40 shadow-[0_6px_16px_-8px_rgba(0,0,0,0.7)]"
        style={{ marginLeft: collapsed ? "56px" : "224px" }}
      >
        <div className="px-6 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={() => navigate("/supplier-security")}
              className={ghostBtn}
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back
            </button>
            <h1 className="text-[20px] font-bold leading-tight tracking-tight">
              Manage Suppliers
            </h1>
            <span className="ml-2 text-[12px] text-neutral-500">
              {rows.length} monitored
            </span>
          </div>
        </div>
      </header>

      <div className="flex">
        <Sidebar
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
          activeKey="supplychain"
          onChange={() => {}}
        />

        <main className="relative flex-1 px-4 md:px-6 pb-10 space-y-4">
          {gateActive && <SupplierUpgradeRequiredCta />}
          {/* Feedback banner */}
          {!gateActive && notice && (
            <div
              role="alert"
              className={`relative rounded-md px-3 py-2 text-[13px] border ${
                notice.kind === "success"
                  ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
                  : "border-red-500/30 bg-red-500/10 text-red-200"
              }`}
            >
              <div className="flex items-start gap-2 pr-6">
                {notice.kind === "success" ? (
                  <CheckCircle2 className="h-4 w-4 mt-[2px]" />
                ) : (
                  <AlertTriangle className="h-4 w-4 mt-[2px]" />
                )}
                <span>{notice.msg}</span>
              </div>
              <button
                type="button"
                onClick={() => setNotice(null)}
                className="absolute right-1.5 top-1.5 rounded-md p-1 hover:bg-white/10"
                aria-label="Dismiss"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {!gateActive && (<>
          {/* Add form */}
          <section className={`${panel}`}>
            <div className="border-b border-sky-900/30 px-4 py-3">
              <div className="text-[14px] font-semibold text-neutral-100">
                Add a supplier
              </div>
              <div className="text-[12px] text-neutral-500 mt-0.5">
                Enter the vendor's primary domain. We'll start monitoring exposed credentials, infostealer hits and breach mentions within minutes.
              </div>
            </div>
            <form onSubmit={handleAdd} className="px-4 py-4">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                <div className="md:col-span-4">
                  <label htmlFor="domain" className={labelClass}>Domain *</label>
                  <input
                    id="domain"
                    type="text"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                    placeholder="acme.com"
                    className={inputClass}
                    required
                  />
                </div>
                <div className="md:col-span-4">
                  <label htmlFor="name" className={labelClass}>Display name</label>
                  <input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Auto-derived from domain"
                    className={inputClass}
                  />
                </div>
                <div className="md:col-span-4">
                  <label htmlFor="industry" className={labelClass}>Industry</label>
                  <select
                    id="industry"
                    value={industry}
                    onChange={(e) => setIndustry(e.target.value)}
                    className={inputClass}
                  >
                    {INDUSTRY_OPTIONS.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-6">
                  <label htmlFor="size" className={labelClass}>Size</label>
                  <select
                    id="size"
                    value={size}
                    onChange={(e) => setSize(e.target.value)}
                    className={inputClass}
                  >
                    {SIZE_OPTIONS.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-6">
                  <label htmlFor="country" className={labelClass}>Country</label>
                  <select
                    id="country"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className={inputClass}
                  >
                    {COUNTRY_OPTIONS.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="mt-4 flex items-center justify-end">
                <button type="submit" className={primaryBtn} disabled={submitting}>
                  {submitting ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Plus className="h-3.5 w-3.5" />
                  )}
                  {submitting ? "Adding…" : "Add Supplier"}
                </button>
              </div>
            </form>
          </section>

          {/* Monitored suppliers list */}
          <section className={`${panel}`}>
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-sky-900/30 px-4 py-3">
              <div className="text-[14px] font-semibold text-neutral-100">
                Monitored suppliers{" "}
                <span className="text-neutral-500 font-normal">
                  ({filtered.length})
                </span>
              </div>
              <div className="relative">
                <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-500" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by name, domain or country"
                  className="rounded-md border border-sky-900/30 bg-[#0b1426] pl-8 pr-3 py-1.5 text-[12px] text-neutral-200 placeholder:text-neutral-600 outline-none focus:border-sky-500/50 transition-colors w-full sm:w-72"
                />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border-separate border-spacing-0">
                <thead className="bg-[#0b1426]/60">
                  <tr>
                    <th className="px-4 py-2.5 text-left text-[11px] uppercase tracking-wider font-medium text-neutral-400">Supplier</th>
                    <th className="px-4 py-2.5 text-left text-[11px] uppercase tracking-wider font-medium text-neutral-400">Domain</th>
                    <th className="px-4 py-2.5 text-left text-[11px] uppercase tracking-wider font-medium text-neutral-400">Size</th>
                    <th className="px-4 py-2.5 text-left text-[11px] uppercase tracking-wider font-medium text-neutral-400">Country</th>
                    <th className="px-4 py-2.5 text-left text-[11px] uppercase tracking-wider font-medium text-neutral-400">Added</th>
                    <th className="px-4 py-2.5 text-right text-[11px] uppercase tracking-wider font-medium text-neutral-400">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r, i) => {
                    const isConfirming = confirmRemove === r.domain;
                    const isRemoving = removingDomain === r.domain;
                    return (
                      <tr
                        key={r.domain}
                        className={`border-b border-sky-900/20 hover:bg-sky-900/15 transition-colors ${i % 2 === 0 ? "bg-transparent" : "bg-[#091020]/40"}`}
                      >
                        <td className="px-4 py-3 border-t border-sky-900/20 text-[13px] text-neutral-100 font-medium">
                          {r.name}
                        </td>
                        <td className="px-4 py-3 border-t border-sky-900/20 text-[13px] text-neutral-400">
                          {r.domain}
                        </td>
                        <td className="px-4 py-3 border-t border-sky-900/20 text-[13px] text-neutral-300 tabular-nums">
                          {r.size}
                        </td>
                        <td className="px-4 py-3 border-t border-sky-900/20">
                          <span className="text-[16px] leading-none mr-1.5" title={r.country}>
                            {FLAG[r.country] || ""}
                          </span>
                          <span className="text-[12px] text-neutral-400">{r.country}</span>
                        </td>
                        <td className="px-4 py-3 border-t border-sky-900/20 text-[13px] text-neutral-400 tabular-nums">
                          {r.added || "—"}
                        </td>
                        <td className="px-4 py-3 border-t border-sky-900/20 text-right">
                          {isConfirming ? (
                            <div className="inline-flex items-center gap-1.5">
                              <span className="text-[12px] text-rose-300">Delete?</span>
                              <button
                                type="button"
                                onClick={() => handleRemove(r.domain)}
                                disabled={isRemoving}
                                className="inline-flex items-center gap-1 rounded-md bg-rose-600/80 hover:bg-rose-600 px-2 py-1 text-[12px] font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {isRemoving && <Loader2 className="h-3 w-3 animate-spin" />}
                                {isRemoving ? "Deleting…" : "Confirm"}
                              </button>
                              <button
                                type="button"
                                onClick={() => setConfirmRemove(null)}
                                disabled={isRemoving}
                                className="inline-flex items-center gap-1 rounded-md border border-sky-900/40 hover:bg-sky-900/30 px-2 py-1 text-[12px] text-neutral-300 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setConfirmRemove(r.domain)}
                              className="inline-flex items-center gap-1 rounded-md border border-rose-500/40 hover:bg-rose-500/15 px-2 py-1 text-[12px] text-rose-300 transition-colors"
                              aria-label={`Delete ${r.name}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                              Delete
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-10 text-center text-[13px] text-neutral-500">
                        {query
                          ? "No suppliers match your search."
                          : "No suppliers are being monitored yet. Add one above to get started."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
          </>)}
        </main>
      </div>
    </div>
  );
}
